import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart2,
  RefreshCw,
  AlertCircle,
  ZoomIn,
  ZoomOut,
  Maximize2
} from 'lucide-react'
import { InvestmentsAPI } from '../services/api'

// Professional Trading Chart Component
export default function TradingChart({ security, onInvest }) {
  const containerRef = useRef(null)
  const canvasRef = useRef(null)
  const overlayCanvasRef = useRef(null)

  const [dimensions, setDimensions] = useState({ width: 800, height: 400 })
  const [chartType, setChartType] = useState('candle')
  const [timeframe, setTimeframe] = useState('1M')
  const [chartData, setChartData] = useState([])
  const [priceInfo, setPriceInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Interactive state
  const [hoveredCandle, setHoveredCandle] = useState(null)
  const [mousePos, setMousePos] = useState(null)
  const [crosshairVisible, setCrosshairVisible] = useState(false)

  // View state - fixed scale that doesn't jump
  const [viewState, setViewState] = useState({
    priceMin: null,
    priceMax: null,
    visibleCandles: 60,
    scrollOffset: 0
  })

  // Chart configuration
  const config = useMemo(() => ({
    padding: { top: 20, right: 80, bottom: 50, left: 10 },
    candleSpacing: 2,
    volumeHeight: 60,
    gridLines: 6,
    colors: {
      background: '#ffffff',
      grid: '#f1f5f9',
      gridText: '#94a3b8',
      bullish: '#10b981',
      bearish: '#ef4444',
      crosshair: '#64748b',
      currentPrice: '#3b82f6',
      volumeBull: 'rgba(16, 185, 129, 0.3)',
      volumeBear: 'rgba(239, 68, 68, 0.3)'
    }
  }), [])

  // Calculate chart dimensions
  const chartArea = useMemo(() => ({
    x: config.padding.left,
    y: config.padding.top,
    width: dimensions.width - config.padding.left - config.padding.right,
    height: dimensions.height - config.padding.top - config.padding.bottom - config.volumeHeight,
    volumeY: dimensions.height - config.padding.bottom - config.volumeHeight,
    volumeHeight: config.volumeHeight
  }), [dimensions, config])

  // Calculate visible data and scales
  const chartScales = useMemo(() => {
    if (!chartData.length) return null

    const visibleData = chartData.slice(-viewState.visibleCandles)
    if (!visibleData.length) return null

    // Calculate price range with padding
    const prices = visibleData.flatMap(d => [parseFloat(d.high), parseFloat(d.low)])
    let minPrice = Math.min(...prices)
    let maxPrice = Math.max(...prices)

    // Add 5% padding to price range
    const priceRange = maxPrice - minPrice || maxPrice * 0.1
    const pricePadding = priceRange * 0.05
    minPrice = minPrice - pricePadding
    maxPrice = maxPrice + pricePadding

    // Use fixed scale if set, otherwise use calculated
    const finalMin = viewState.priceMin !== null ? viewState.priceMin : minPrice
    const finalMax = viewState.priceMax !== null ? viewState.priceMax : maxPrice

    // Volume scale
    const volumes = visibleData.map(d => parseInt(d.volume) || 0)
    const maxVolume = Math.max(...volumes) || 1

    // Candle width calculation
    const candleWidth = Math.max(4, (chartArea.width / visibleData.length) - config.candleSpacing)

    return {
      visibleData,
      priceMin: finalMin,
      priceMax: finalMax,
      priceRange: finalMax - finalMin,
      maxVolume,
      candleWidth,
      // Scale functions
      priceToY: (price) => {
        return chartArea.y + chartArea.height - ((price - finalMin) / (finalMax - finalMin)) * chartArea.height
      },
      yToPrice: (y) => {
        return finalMin + ((chartArea.y + chartArea.height - y) / chartArea.height) * (finalMax - finalMin)
      },
      indexToX: (index) => {
        return chartArea.x + (index * (candleWidth + config.candleSpacing)) + candleWidth / 2
      },
      xToIndex: (x) => {
        const relX = x - chartArea.x
        return Math.floor(relX / (candleWidth + config.candleSpacing))
      },
      volumeToHeight: (volume) => {
        return (volume / maxVolume) * chartArea.volumeHeight * 0.8
      }
    }
  }, [chartData, viewState, chartArea, config])

  // Map timeframe button to API interval
  const getIntervalFromTimeframe = useCallback((tf) => {
    const mapping = {
      '1M': '1m',
      '5M': '5m',
      '15M': '15m',
      '1H': '1h',
      '4H': '4h',
      '1D': '1d'
    }
    return mapping[tf] || '1m'
  }, [])

  // Fetch price history from API
  const fetchPriceHistory = useCallback(async (isInitial = false, newTimeframe = null) => {
    if (!security?.symbol) return

    try {
      if (isInitial) setLoading(true)
      setError(null)

      const interval = getIntervalFromTimeframe(newTimeframe || timeframe)
      const response = await InvestmentsAPI.getPriceHistory(security.symbol, interval, 200)

      if (response.success && response.data) {
        const { history, currentPrice, previousClose, dayHigh, dayLow, changeAmount, changePercent } = response.data

        setChartData(prev => {
          if (!isInitial && prev.length > 0 && history.length > 0) {
            // Smart merge - only add new candles or update the last one
            const lastPrevTime = prev[prev.length - 1]?.time
            const newCandles = history.filter(h => h.time > lastPrevTime)

            if (newCandles.length === 0) {
              // Update last candle with new price
              const updated = [...prev]
              const lastCandle = updated[updated.length - 1]
              const newLastCandle = history[history.length - 1]
              if (newLastCandle) {
                updated[updated.length - 1] = {
                  ...lastCandle,
                  high: Math.max(parseFloat(lastCandle.high), parseFloat(newLastCandle.high)),
                  low: Math.min(parseFloat(lastCandle.low), parseFloat(newLastCandle.low)),
                  close: parseFloat(newLastCandle.close),
                  volume: parseInt(newLastCandle.volume) || lastCandle.volume
                }
              }
              return updated
            } else {
              // Add new candles
              return [...prev.slice(-(200 - newCandles.length)), ...newCandles]
            }
          }
          return history || []
        })

        setPriceInfo({
          currentPrice,
          previousClose,
          dayHigh,
          dayLow,
          changeAmount,
          changePercent
        })
      }
    } catch (err) {
      console.error('Error fetching price history:', err)
      if (isInitial) setError('Failed to load chart data')
    } finally {
      if (isInitial) setLoading(false)
    }
  }, [security?.symbol, timeframe, getIntervalFromTimeframe])

  // Initial fetch
  useEffect(() => {
    if (security?.symbol) {
      fetchPriceHistory(true)
    }
  }, [security?.symbol, fetchPriceHistory])

  // Refetch when timeframe changes
  useEffect(() => {
    if (security?.symbol && !loading) {
      setChartData([]) // Clear old data
      fetchPriceHistory(true)
    }
  }, [timeframe])

  // Polling for updates
  useEffect(() => {
    if (!security?.symbol || loading) return

    const interval = setInterval(() => fetchPriceHistory(false), 5000)
    return () => clearInterval(interval)
  }, [security?.symbol, loading, fetchPriceHistory])

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setDimensions({
          width: rect.width,
          height: 400
        })
      }
    }

    updateDimensions()
    const observer = new ResizeObserver(updateDimensions)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Draw main chart
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !chartScales) return

    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1

    // Setup canvas for retina
    canvas.width = dimensions.width * dpr
    canvas.height = dimensions.height * dpr
    ctx.scale(dpr, dpr)

    // Clear
    ctx.fillStyle = config.colors.background
    ctx.fillRect(0, 0, dimensions.width, dimensions.height)

    const { visibleData, priceMin, priceMax, priceRange, candleWidth } = chartScales

    // Draw grid lines
    ctx.strokeStyle = config.colors.grid
    ctx.lineWidth = 1
    ctx.setLineDash([])

    // Horizontal grid lines (price levels)
    const priceStep = priceRange / config.gridLines
    for (let i = 0; i <= config.gridLines; i++) {
      const price = priceMin + (priceStep * i)
      const y = chartScales.priceToY(price)

      ctx.beginPath()
      ctx.moveTo(chartArea.x, y)
      ctx.lineTo(chartArea.x + chartArea.width, y)
      ctx.stroke()

      // Price label
      ctx.fillStyle = config.colors.gridText
      ctx.font = '11px -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.textAlign = 'left'
      ctx.fillText(`$${price.toFixed(2)}`, chartArea.x + chartArea.width + 8, y + 4)
    }

    // Draw volume bars
    visibleData.forEach((candle, i) => {
      const x = chartScales.indexToX(i)
      const volume = parseInt(candle.volume) || 0
      const barHeight = chartScales.volumeToHeight(volume)
      const open = parseFloat(candle.open)
      const close = parseFloat(candle.close)
      const isBullish = close >= open

      ctx.fillStyle = isBullish ? config.colors.volumeBull : config.colors.volumeBear
      ctx.fillRect(
        x - candleWidth / 2,
        chartArea.volumeY + chartArea.volumeHeight - barHeight,
        candleWidth,
        barHeight
      )
    })

    // Draw candles or line
    if (chartType === 'candle') {
      visibleData.forEach((candle, i) => {
        const x = chartScales.indexToX(i)
        const open = parseFloat(candle.open)
        const close = parseFloat(candle.close)
        const high = parseFloat(candle.high)
        const low = parseFloat(candle.low)
        const isBullish = close >= open
        const color = isBullish ? config.colors.bullish : config.colors.bearish

        const yOpen = chartScales.priceToY(open)
        const yClose = chartScales.priceToY(close)
        const yHigh = chartScales.priceToY(high)
        const yLow = chartScales.priceToY(low)

        // Wick
        ctx.strokeStyle = color
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(x, yHigh)
        ctx.lineTo(x, yLow)
        ctx.stroke()

        // Body
        const bodyTop = Math.min(yOpen, yClose)
        const bodyHeight = Math.max(1, Math.abs(yClose - yOpen))

        ctx.fillStyle = color
        ctx.fillRect(x - candleWidth / 2, bodyTop, candleWidth, bodyHeight)
      })
    } else {
      // Line chart
      if (visibleData.length > 1) {
        const firstClose = parseFloat(visibleData[0].close)
        const lastClose = parseFloat(visibleData[visibleData.length - 1].close)
        const isBullish = lastClose >= firstClose
        const lineColor = isBullish ? config.colors.bullish : config.colors.bearish

        // Area gradient
        const gradient = ctx.createLinearGradient(0, chartArea.y, 0, chartArea.y + chartArea.height)
        gradient.addColorStop(0, isBullish ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)')
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')

        // Draw area
        ctx.beginPath()
        ctx.moveTo(chartScales.indexToX(0), chartScales.priceToY(parseFloat(visibleData[0].close)))
        visibleData.forEach((candle, i) => {
          ctx.lineTo(chartScales.indexToX(i), chartScales.priceToY(parseFloat(candle.close)))
        })
        ctx.lineTo(chartScales.indexToX(visibleData.length - 1), chartArea.y + chartArea.height)
        ctx.lineTo(chartScales.indexToX(0), chartArea.y + chartArea.height)
        ctx.closePath()
        ctx.fillStyle = gradient
        ctx.fill()

        // Draw line
        ctx.beginPath()
        ctx.moveTo(chartScales.indexToX(0), chartScales.priceToY(parseFloat(visibleData[0].close)))
        visibleData.forEach((candle, i) => {
          ctx.lineTo(chartScales.indexToX(i), chartScales.priceToY(parseFloat(candle.close)))
        })
        ctx.strokeStyle = lineColor
        ctx.lineWidth = 2
        ctx.stroke()
      }
    }

    // Draw current price line
    if (priceInfo?.currentPrice) {
      const currentY = chartScales.priceToY(priceInfo.currentPrice)

      ctx.setLineDash([4, 4])
      ctx.strokeStyle = config.colors.currentPrice
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(chartArea.x, currentY)
      ctx.lineTo(chartArea.x + chartArea.width, currentY)
      ctx.stroke()
      ctx.setLineDash([])

      // Price label box
      ctx.fillStyle = config.colors.currentPrice
      const labelWidth = 70
      const labelHeight = 20
      ctx.fillRect(chartArea.x + chartArea.width + 2, currentY - labelHeight / 2, labelWidth, labelHeight)

      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(`$${priceInfo.currentPrice.toFixed(2)}`, chartArea.x + chartArea.width + 2 + labelWidth / 2, currentY + 4)
    }

    // Time labels - format based on timeframe
    ctx.fillStyle = config.colors.gridText
    ctx.font = '10px -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.textAlign = 'center'

    const formatTimeLabel = (timestamp) => {
      const time = new Date(timestamp)
      if (timeframe === '1D') {
        return time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      } else if (timeframe === '4H' || timeframe === '1H') {
        return `${time.getMonth() + 1}/${time.getDate()} ${time.getHours()}:00`
      } else {
        return `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`
      }
    }

    const timeStep = Math.ceil(visibleData.length / 6)
    visibleData.forEach((candle, i) => {
      if (i % timeStep === 0) {
        const x = chartScales.indexToX(i)
        const label = formatTimeLabel(candle.time)
        ctx.fillText(label, x, dimensions.height - config.padding.bottom + 15)
      }
    })

  }, [chartData, chartScales, chartType, dimensions, config, chartArea, priceInfo, timeframe])

  // Draw overlay (crosshair, tooltip)
  useEffect(() => {
    const overlay = overlayCanvasRef.current
    if (!overlay || !chartScales) return

    const ctx = overlay.getContext('2d')
    const dpr = window.devicePixelRatio || 1

    overlay.width = dimensions.width * dpr
    overlay.height = dimensions.height * dpr
    ctx.scale(dpr, dpr)

    // Clear overlay
    ctx.clearRect(0, 0, dimensions.width, dimensions.height)

    if (!crosshairVisible || !mousePos) return

    const { x, y } = mousePos
    const { visibleData, candleWidth } = chartScales

    // Check if mouse is in chart area
    if (x < chartArea.x || x > chartArea.x + chartArea.width ||
        y < chartArea.y || y > chartArea.y + chartArea.height + chartArea.volumeHeight) {
      return
    }

    // Draw crosshair lines
    ctx.setLineDash([3, 3])
    ctx.strokeStyle = config.colors.crosshair
    ctx.lineWidth = 1

    // Vertical line
    ctx.beginPath()
    ctx.moveTo(x, chartArea.y)
    ctx.lineTo(x, chartArea.y + chartArea.height + chartArea.volumeHeight)
    ctx.stroke()

    // Horizontal line
    ctx.beginPath()
    ctx.moveTo(chartArea.x, y)
    ctx.lineTo(chartArea.x + chartArea.width, y)
    ctx.stroke()

    ctx.setLineDash([])

    // Get candle under cursor
    const candleIndex = chartScales.xToIndex(x)
    const candle = visibleData[candleIndex]

    if (candle) {
      const candleX = chartScales.indexToX(candleIndex)

      // Highlight candle
      ctx.strokeStyle = config.colors.currentPrice
      ctx.lineWidth = 2
      ctx.strokeRect(
        candleX - candleWidth / 2 - 2,
        chartArea.y,
        candleWidth + 4,
        chartArea.height
      )

      // Draw tooltip
      const tooltipWidth = 180
      const tooltipHeight = 130
      let tooltipX = candleX + 15
      let tooltipY = y - tooltipHeight / 2

      // Keep tooltip in bounds
      if (tooltipX + tooltipWidth > dimensions.width - config.padding.right) {
        tooltipX = candleX - tooltipWidth - 15
      }
      if (tooltipY < chartArea.y) tooltipY = chartArea.y
      if (tooltipY + tooltipHeight > chartArea.y + chartArea.height) {
        tooltipY = chartArea.y + chartArea.height - tooltipHeight
      }

      // Tooltip background with rounded corners
      ctx.fillStyle = 'rgba(15, 23, 42, 0.95)'
      ctx.beginPath()
      const radius = 8
      ctx.moveTo(tooltipX + radius, tooltipY)
      ctx.lineTo(tooltipX + tooltipWidth - radius, tooltipY)
      ctx.quadraticCurveTo(tooltipX + tooltipWidth, tooltipY, tooltipX + tooltipWidth, tooltipY + radius)
      ctx.lineTo(tooltipX + tooltipWidth, tooltipY + tooltipHeight - radius)
      ctx.quadraticCurveTo(tooltipX + tooltipWidth, tooltipY + tooltipHeight, tooltipX + tooltipWidth - radius, tooltipY + tooltipHeight)
      ctx.lineTo(tooltipX + radius, tooltipY + tooltipHeight)
      ctx.quadraticCurveTo(tooltipX, tooltipY + tooltipHeight, tooltipX, tooltipY + tooltipHeight - radius)
      ctx.lineTo(tooltipX, tooltipY + radius)
      ctx.quadraticCurveTo(tooltipX, tooltipY, tooltipX + radius, tooltipY)
      ctx.closePath()
      ctx.fill()

      // Tooltip content
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, sans-serif'

      const time = new Date(candle.time)
      const timeStr = time.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })

      ctx.fillText(timeStr, tooltipX + 12, tooltipY + 24)

      ctx.font = '11px -apple-system, BlinkMacSystemFont, sans-serif'
      const open = parseFloat(candle.open)
      const high = parseFloat(candle.high)
      const low = parseFloat(candle.low)
      const close = parseFloat(candle.close)
      const volume = parseInt(candle.volume) || 0
      const change = close - open
      const changePct = open > 0 ? (change / open) * 100 : 0
      const isBullish = close >= open

      const labels = [
        { label: 'Open', value: `$${open.toFixed(2)}`, color: '#94a3b8' },
        { label: 'High', value: `$${high.toFixed(2)}`, color: '#10b981' },
        { label: 'Low', value: `$${low.toFixed(2)}`, color: '#ef4444' },
        { label: 'Close', value: `$${close.toFixed(2)}`, color: '#ffffff' },
        { label: 'Change', value: `${change >= 0 ? '+' : ''}${change.toFixed(2)} (${changePct.toFixed(2)}%)`, color: isBullish ? '#10b981' : '#ef4444' },
      ]

      labels.forEach((item, i) => {
        const rowY = tooltipY + 44 + (i * 18)
        ctx.fillStyle = '#64748b'
        ctx.fillText(item.label, tooltipX + 12, rowY)
        ctx.fillStyle = item.color
        ctx.textAlign = 'right'
        ctx.fillText(item.value, tooltipX + tooltipWidth - 12, rowY)
        ctx.textAlign = 'left'
      })

      // Volume
      ctx.fillStyle = '#64748b'
      ctx.fillText('Volume', tooltipX + 12, tooltipY + 118)
      ctx.fillStyle = '#94a3b8'
      ctx.textAlign = 'right'
      ctx.fillText(volume.toLocaleString(), tooltipX + tooltipWidth - 12, tooltipY + 118)
      ctx.textAlign = 'left'

      setHoveredCandle(candle)
    } else {
      setHoveredCandle(null)
    }

    // Price at cursor
    const priceAtCursor = chartScales.yToPrice(y)
    if (y >= chartArea.y && y <= chartArea.y + chartArea.height) {
      ctx.fillStyle = 'rgba(100, 116, 139, 0.9)'
      ctx.fillRect(chartArea.x + chartArea.width + 2, y - 10, 70, 20)
      ctx.fillStyle = '#ffffff'
      ctx.font = '11px -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(`$${priceAtCursor.toFixed(2)}`, chartArea.x + chartArea.width + 37, y + 4)
    }

  }, [mousePos, crosshairVisible, chartScales, dimensions, config, chartArea])

  // Mouse handlers
  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
  }, [])

  const handleMouseEnter = useCallback(() => {
    setCrosshairVisible(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setCrosshairVisible(false)
    setMousePos(null)
    setHoveredCandle(null)
  }, [])

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      visibleCandles: Math.max(20, prev.visibleCandles - 10)
    }))
  }, [])

  const handleZoomOut = useCallback(() => {
    setViewState(prev => ({
      ...prev,
      visibleCandles: Math.min(200, prev.visibleCandles + 10)
    }))
  }, [])

  const handleResetView = useCallback(() => {
    setViewState({
      priceMin: null,
      priceMax: null,
      visibleCandles: 60,
      scrollOffset: 0
    })
  }, [])

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 5 : -5
    setViewState(prev => ({
      ...prev,
      visibleCandles: Math.max(20, Math.min(200, prev.visibleCandles + delta))
    }))
  }, [])

  if (!security) return null

  const currentPrice = priceInfo?.currentPrice || parseFloat(security.current_price) || 0
  const changePercent = priceInfo?.changePercent || parseFloat(security.change_percent) || 0
  const changeAmount = priceInfo?.changeAmount || parseFloat(security.change_amount) || 0
  const isPositive = changePercent >= 0

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
              <span className="text-slate-900 font-bold text-lg">
                {security.symbol?.charAt(0)}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-slate-900">{security.symbol}</h3>
                <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                  {security.type || security.security_type || 'Stock'}
                </span>
              </div>
              <p className="text-sm text-slate-500">{security.name}</p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900">
              ${currentPrice.toFixed(2)}
            </p>
            <div className={`flex items-center justify-end gap-1 ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
              {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span className="text-sm font-medium">
                {isPositive ? '+' : ''}{changeAmount.toFixed(2)} ({changePercent.toFixed(2)}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Controls */}
      <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-1">
          {['1M', '5M', '15M', '1H', '4H', '1D'].map(tf => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                timeframe === tf
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={handleZoomIn}
            className="p-2 text-slate-600 hover:bg-slate-200 rounded transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 text-slate-600 hover:bg-slate-200 rounded transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={handleResetView}
            className="p-2 text-slate-600 hover:bg-slate-200 rounded transition-colors"
            title="Reset View"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-slate-200 mx-1" />
          <button
            onClick={() => fetchPriceHistory(false)}
            className="p-2 text-slate-600 hover:bg-slate-200 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setChartType('line')}
            className={`p-2 rounded transition-colors ${
              chartType === 'line' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
            }`}
            title="Line Chart"
          >
            <Activity className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType('candle')}
            className={`p-2 rounded transition-colors ${
              chartType === 'candle' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
            }`}
            title="Candlestick Chart"
          >
            <BarChart2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Chart */}
      <div
        ref={containerRef}
        className="relative cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      >
        {error ? (
          <div className="flex items-center justify-center h-[400px] text-slate-500">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p>{error}</p>
            </div>
          </div>
        ) : chartData.length === 0 && !loading ? (
          <div className="flex items-center justify-center h-[400px] text-slate-500">
            <div className="text-center">
              <BarChart2 className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p>No chart data available</p>
            </div>
          </div>
        ) : (
          <>
            <canvas
              ref={canvasRef}
              style={{ width: dimensions.width, height: dimensions.height }}
              className="block"
            />
            <canvas
              ref={overlayCanvasRef}
              style={{ width: dimensions.width, height: dimensions.height }}
              className="absolute top-0 left-0 pointer-events-none"
            />

            {/* Live indicator */}
            <div className="absolute top-4 left-4">
              <span className="flex items-center gap-1.5 px-2 py-1 bg-slate-900/80 backdrop-blur rounded-full text-xs text-white">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                LIVE
              </span>
            </div>

            {/* OHLC Display when hovering */}
            {hoveredCandle && (
              <div className="absolute top-4 left-20 flex items-center gap-4 px-3 py-1.5 bg-white/90 backdrop-blur rounded-lg text-xs shadow-sm border border-slate-200">
                <span className="text-slate-500">O: <span className="text-slate-900 font-medium">${parseFloat(hoveredCandle.open).toFixed(2)}</span></span>
                <span className="text-slate-500">H: <span className="text-emerald-600 font-medium">${parseFloat(hoveredCandle.high).toFixed(2)}</span></span>
                <span className="text-slate-500">L: <span className="text-red-600 font-medium">${parseFloat(hoveredCandle.low).toFixed(2)}</span></span>
                <span className="text-slate-500">C: <span className="text-slate-900 font-medium">${parseFloat(hoveredCandle.close).toFixed(2)}</span></span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 p-4 border-t border-slate-100 bg-slate-50">
        <div>
          <p className="text-xs text-slate-500">Day High</p>
          <p className="text-sm font-semibold text-slate-900">
            ${(priceInfo?.dayHigh || currentPrice).toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Day Low</p>
          <p className="text-sm font-semibold text-slate-900">
            ${(priceInfo?.dayLow || currentPrice).toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Prev Close</p>
          <p className="text-sm font-semibold text-slate-900">
            ${(priceInfo?.previousClose || currentPrice).toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Candles</p>
          <p className="text-sm font-semibold text-slate-900">{viewState.visibleCandles}</p>
        </div>
      </div>

      {/* Action Button */}
      {onInvest && (
        <div className="p-4 border-t border-slate-100">
          <button
            onClick={() => onInvest(security)}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <TrendingUp className="w-5 h-5" />
            Invest in {security.symbol}
          </button>
        </div>
      )}
    </div>
  )
}
